import { useEffect, useMemo, useRef, useState } from 'react';
import type { DeckSummary } from '../../shared/deck.ts';

type BulkCategoryModalProps = {
  decks: DeckSummary[];
  allCategories?: string[];
  busy?: boolean;
  onCancel: () => void;
  onSave: (next: { category: string | null }) => void;
};

/**
 * Move MANY decks to one category at once (dashboard multi-select).
 *
 * A deck has exactly one home (`DeckSummary.category`), so this is a plain
 * single choice: pick a row, or type a path, and every selected deck moves
 * there. "Uncategorised" clears them. The tri-state, delta-based version this
 * replaced (2026-09) belonged to the multi-category model and had no meaning
 * once a deck could not be in two places.
 *
 * `onSave({ category })` — null means Uncategorised; the caller sends the move
 * to `POST /api/decks/bulk` in one request.
 */
export default function BulkCategoryModal({
  decks,
  allCategories = [],
  busy = false,
  onCancel,
  onSave,
}: BulkCategoryModalProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState('');
  // The picked row, in its canonical casing; '' is Uncategorised, null is
  // "nothing picked yet" (the typed path counts instead).
  const [picked, setPicked] = useState<string | null>(null);

  // Every category the rows should offer: the registry's, plus any a selected
  // deck already sits in that has since left the registry.
  const options = useMemo(() => {
    const out = [...allCategories];
    const seen = new Set(out.map((p) => p.toLowerCase()));
    for (const d of decks) {
      if (d.category && !seen.has(d.category.toLowerCase())) {
        seen.add(d.category.toLowerCase());
        out.push(d.category);
      }
    }
    return out;
  }, [allCategories, decks]);

  // Where the selection sits now, for the "(current)" hint: one path if every
  // deck agrees, otherwise nothing is marked.
  const shared = useMemo(() => {
    const homes = new Set(decks.map((d) => (d.category || '').toLowerCase()));
    return homes.size === 1 ? [...homes][0] : null;
  }, [decks]);

  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Same contract as the single-deck dialog: 1–2 non-empty segments, ≤30 chars
  // each. Empty → null (nothing typed), invalid → undefined.
  function normalise(raw: string): string | null | undefined {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const segs = trimmed
      .split('/')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!segs.length || segs.length > 2 || segs.some((s) => s.length > 30)) return undefined;
    return segs.join('/');
  }
  const normalised = normalise(value);
  const invalid = normalised === undefined;

  // A typed path outranks a picked row: typing is the more deliberate act, and
  // it is how a category that does not exist yet gets made.
  const destination: string | null | undefined = normalised ? normalised : picked === null ? undefined : picked || null;
  const unchanged = destination !== undefined && shared !== null && (destination || '').toLowerCase() === shared;

  function submit() {
    if (invalid || destination === undefined || unchanged) return;
    onSave({ category: destination });
  }

  return (
    <div className="modal-backdrop" onMouseDown={onCancel}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-bulkcat-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <h3 className="modal__title modal__title--sm" id="modal-bulkcat-title">
            Move to category
          </h3>
        </header>
        <div className="modal__body">
          <p className="muted small" style={{ marginBottom: '0.6rem' }}>
            {decks.length} deck{decks.length === 1 ? '' : 's'} selected
          </p>
          <ul className="cat-picker" role="list">
            {options.map((c) => {
              const key = c.toLowerCase();
              return (
                <li key={c}>
                  <label className="cat-picker__row">
                    <input
                      type="radio"
                      name="bulk-category"
                      checked={!normalised && picked !== null && picked.toLowerCase() === key}
                      onChange={() => {
                        setPicked(c);
                        setValue('');
                      }}
                    />
                    <span>{c.replace('/', ' › ')}</span>
                    {shared === key && <span className="muted small">current</span>}
                  </label>
                </li>
              );
            })}
            <li>
              <label className="cat-picker__row">
                <input
                  type="radio"
                  name="bulk-category"
                  checked={!normalised && picked === ''}
                  onChange={() => {
                    setPicked('');
                    setValue('');
                  }}
                />
                <span>Uncategorised</span>
                {shared === '' && <span className="muted small">current</span>}
              </label>
            </li>
          </ul>
          <input
            ref={inputRef}
            type="text"
            className="modal__input"
            value={value}
            placeholder="Or a new one, e.g. Clients/Acme"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />
          <p className="muted small" style={{ marginTop: '0.5rem' }}>
            {invalid
              ? 'Use one or two levels (Parent/Child), each up to 30 characters.'
              : 'Every selected deck moves to the category you pick. A deck has one category; its tags stay as they are.'}
          </p>
        </div>
        <footer className="modal__footer">
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={submit}
            disabled={busy || invalid || destination === undefined || unchanged}
          >
            {destination === null ? 'Clear category' : 'Move'}
          </button>
        </footer>
      </div>
    </div>
  );
}
